import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Car, CreateCarDto } from '../api/carsApi';
import Label from '@/components/form/Label';
import Input from '@/components/form/input/InputField';
import Select from '@/components/form/Select';
import Button from '@/components/ui/button/Button';
import { useCarEnums } from '../hooks/useCars';
import { useToast } from '@/components/ui/toast/Toast';
import Checkbox from '@/components/form/input/Checkbox';
import { CAR_COLORS } from '@/shared/constants/colors';
import TextArea from '@/components/form/input/TextArea';
import { useCarModels } from '@/features/car-models/hooks/useCarModels';
import { withFormProtection } from '@/components/form/withFormProtection';
import ImagePreview from '@/components/ui/images/ImagePreview';
import { useTranslation } from 'react-i18next';

const defaultFormData: CreateCarDto = {
  model: '',
  slug: '',
  color: '',
  registration_number: '',
  release_year: new Date().getFullYear(),
  description_en: '',
  description_fr: '',
  price: '',
  ecology_class: '',
  fuel_type: 'petrol',
  mileage_limit: 0,
  drivetrain: 'front_2wd',
  fuel_consumption: 0,
  engine_displacement: 0,
  engine_power: 0,
  seats_count: 0,
  doors_count: 0,
  has_gps: false,
  clearance: 0,
  permitted_area_latitude: 0,
  permitted_area_longitude: 0,
  permitted_area_radius: 0,
};

interface CarCreateFormProps {
  onSubmit: (data: CreateCarDto) => Promise<void>;
  onClose: () => void;
  initialData: CreateCarDto;
  currentData: CreateCarDto;
  onDataChange?: (data: CreateCarDto) => void;
}

interface FormErrors {
  model?: string;
  registration_number?: string;
  color?: string;
  price?: string;
  slug?: string;
}

const CarCreateForm = React.forwardRef<{ handleClose: () => void; isDirty: () => boolean }, CarCreateFormProps>(
  ({ onSubmit, onClose, initialData, onDataChange }, ref) => {
    const { t } = useTranslation();
    const toast = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const { data: carModelsResponse } = useCarModels(1);
    const carModels = carModelsResponse?.data || [];
    const [errors, setErrors] = useState<FormErrors>({});
    const [formData, setFormData] = useState<CreateCarDto>(initialData || defaultFormData);

    useEffect(() => {
      onDataChange?.(formData);
    }, [formData, onDataChange]);

    const generateSlug = (str: string) => {
      return str
        .toLowerCase()
        .replace(/\s+/g, '-')           // Replace spaces with -
        .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
        .replace(/\-\-+/g, '-')         // Replace multiple - with single -
        .replace(/^-+/, '')             // Trim - from start of text
        .replace(/-+$/, '');            // Trim - from end of text
    };

    const { data: enums } = useCarEnums();

    const validateForm = (): boolean => {
      const newErrors: FormErrors = {};

      if (!formData.model) {
        newErrors.model = t('cars.create.errors.model_required');
      }
      if (!formData.registration_number.trim()) {
        newErrors.registration_number = t('cars.create.errors.reg_number_required');
      }
      if (!formData.color) {
        newErrors.color = t('cars.create.errors.color_required');
      }
      if (!formData.price || isNaN(Number(formData.price))) {
        newErrors.price = t('cars.create.errors.price_required');
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const clearFieldError = (field: keyof FormErrors) => {
      if (errors[field]) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    };

    const handleModelChange = (value: string) => {
      setFormData(prev => ({ ...prev, model: value }));
      if (value) {
        clearFieldError('model');
      }
    };

    const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFormData(prev => ({ ...prev, slug: value }));
      if (value.trim()) {
        clearFieldError('slug');
      }
    };

    const handleRegistrationNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFormData(prev => ({ ...prev, registration_number: value }));
      if (value.trim()) {
        clearFieldError('registration_number');
      }
    };

    const handleColorChange = (value: string) => {
      setFormData(prev => ({ ...prev, color: value }));
      if (value) {
        clearFieldError('color');
      }
    };

    const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFormData(prev => ({ ...prev, price: value }));
      if (value && !isNaN(Number(value))) {
        clearFieldError('price');
      }
    };

    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        setSelectedImage(file);
      }
    };

    const handleImageClick = () => {
      fileInputRef.current?.click();
    };

    const handleRemoveImage = (e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedImage(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = ''; // Reset the file input value
      }
    };

    const handleSubmit = async () => {
      if (!validateForm()) {
        return;
      }

      try {
        if (!formData.model) {
          throw new Error('Please select a car model');
        }

        if (!formData.fuel_type) {
          throw new Error('Please select a fuel type');
        }

        if (!formData.drivetrain) {
          throw new Error('Please select a drivetrain');
        }

        if (!formData.price || isNaN(Number(formData.price))) {
          throw new Error('Please enter a valid price');
        }

        // Генерируем slug из регистрационного номера, если он не был задан
        const dataToSubmit = {
          ...formData,
          slug: formData.slug || generateSlug(formData.registration_number),
          image: selectedImage || undefined
        };

        await onSubmit(dataToSubmit);
        toast.success(t('cars.create.success'));
      } catch (error) {
        toast.error(t('cars.create.error'));
      }
    };

    return (
      <>
        <div className="px-6 py-4">
          <h5 className="text-xl font-semibold text-text-primary lg:text-2xl">
            {t('cars.create.title')}
          </h5>
          <p className="text-sm text-text-tertiary">
            {t('cars.create.subtitle')}
          </p>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-[-8px] mb-[-18px]">
            {/* Basic Information */}
            <div className="col-span-1">
              <h3 className="text-lg font-medium mb-4 text-text-primary">{t('cars.create.basic_info')}</h3>

              <div className="grid grid-cols-[auto,1fr] gap-4">
                <div className="col-span-2 md:col-span-1">
                  <Label>{t('cars.create.fields.car_image')}</Label>
                  <div className="mt-2 flex items-center gap-4">
                    <div
                      className="relative w-32 h-32 border-[1px] border-border-secondary rounded-lg overflow-hidden cursor-pointer"
                      onClick={handleImageClick}
                    >
                      {selectedImage ? (
                        <div className="relative group w-full h-full">
                          <div
                            className="w-full h-full flex"
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <ImagePreview imageUrl={URL.createObjectURL(selectedImage)}>
                              <img
                                src={URL.createObjectURL(selectedImage)}
                                alt="Car preview"
                                className="w-full h-full object-cover"
                              />
                            </ImagePreview>
                          </div>
                          <button
                            onClick={handleRemoveImage}
                            className="absolute top-2 right-2 z-10 bg-bg-secondary rounded-full p-1 shadow-md hover:bg-bg-hover"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4 text-gray-500"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <div className="relative w-32 h-32">
                          <div className="absolute inset-0 flex items-center justify-center bg-bg-tertiary bg-opacity-50 text-text-muted">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-6 w-6"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 4v16m8-8H4"
                              />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      data-testid="car-form-field-image"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </div>
                </div>

                <div className="col-span-2 md:col-span-1">
                  <div className="grid grid-cols-2 gap-4">

                    <div className="col-span-2 md:col-span-1">
                      <Label>{t('cars.create.fields.car_model')} *</Label>
                      <Select
                        testId="car-form-field-model"
                        defaultValue={formData.model}
                        onChange={handleModelChange}
                        options={carModels.map(model => ({
                          label: `${model.brand_name} ${model.model_name}`,
                          value: model.id
                        }))}
                        placeholder={t('cars.create.placeholders.select_model')}
                        error={errors.model}
                      />
                    </div>

                    <div className="col-span-2 md:col-span-1">
                      <Label>{t('cars.create.fields.reg_number')} *</Label>
                      <Input
                        testId="car-form-field-registration-number"
                        type="text"
                        value={formData.registration_number}
                        onChange={handleRegistrationNumberChange}
                        placeholder={t('cars.create.placeholders.enter_reg_number')}
                        error={errors.registration_number}
                      />
                    </div>
                  </div>

                  <div className="col-span-2 md:col-span-1 mt-4">
                    <Label>{t('cars.create.fields.slug')} *</Label>
                    <Input
                      testId="car-form-field-slug"
                      type="text"
                      value={formData.slug}
                      onChange={handleSlugChange}
                      placeholder={t('cars.create.placeholders.enter_slug')}
                      error={errors.slug}
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="col-span-3 md:col-span-1">
                  <Label>{t('cars.create.fields.color')} *</Label>
                  <Select
                    testId="car-form-field-color"
                    defaultValue={formData.color}
                    onChange={handleColorChange}
                    options={CAR_COLORS}
                    placeholder={t('cars.create.placeholders.select_color')}
                    error={errors.color}
                    iconTemplate={(value) => (
                      <div
                        className="w-4 h-4 rounded-full border border-gray-400 mr-2"
                        style={{ backgroundColor: value }}
                      />
                    )}
                  />
                </div>

                <div className="col-span-3 md:col-span-1">
                  <Label>{t('cars.create.fields.release_year')}</Label>
                  <Input
                    testId="car-form-field-release-year"
                    type="number"
                    value={formData.release_year}
                    onChange={(e) => setFormData(prev => ({ ...prev, release_year: parseInt(e.target.value) }))}
                    placeholder={t('cars.create.placeholders.enter_year')}
                  />
                </div>

                <div className="col-span-3 md:col-span-1">
                  <Label>{t('cars.create.fields.price')} *</Label>
                  <Input
                    testId="car-form-field-price"
                    type="text"
                    value={formData.price}
                    onChange={handlePriceChange}
                    placeholder={t('cars.create.placeholders.enter_price')}
                    error={errors.price}
                  />
                </div>
              </div>
            </div>

            {/* Technical Specifications */}
            <div className="col-span-1">
              <h3 className="text-lg font-medium mb-4 text-text-primary">{t('cars.create.technical_specs')}</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>{t('cars.create.fields.fuel_type')}</Label>
                  <Select
                    testId="car-form-field-fuel_type"
                    defaultValue={formData.fuel_type}
                    onChange={(value) => setFormData(prev => ({ ...prev, fuel_type: value as typeof formData.fuel_type }))}
                    options={enums?.fuel_type || []}
                    placeholder={t('cars.create.placeholders.select_fuel_type')}
                  />
                </div>

                <div>
                  <Label>{t('cars.create.fields.drivetrain')}</Label>
                  <Select
                    testId="car-form-field-drivetrain"
                    defaultValue={formData.drivetrain}
                    onChange={(value) => setFormData(prev => ({ ...prev, drivetrain: value as typeof formData.drivetrain }))}
                    options={enums?.drivetrain || []}
                    placeholder={t('cars.create.placeholders.select_drivetrain')}
                  />
                </div>

                <div>
                  <Label>{t('cars.create.fields.engine_displacement')}</Label>
                  <Input
                    testId="car-form-field-engine_displacement"
                    type="number"
                    value={formData.engine_displacement}
                    onChange={(e) => setFormData(prev => ({ ...prev, engine_displacement: parseFloat(e.target.value) }))}
                    placeholder={t('cars.create.placeholders.enter_engine')}
                  />
                </div>

                <div>
                  <Label>{t('cars.create.fields.engine_power')}</Label>
                  <Input
                    testId="car-form-field-engine_power"
                    type="number"
                    value={formData.engine_power}
                    onChange={(e) => setFormData(prev => ({ ...prev, engine_power: parseFloat(e.target.value) }))}
                    placeholder={t('cars.create.placeholders.enter_power')}
                  />
                </div>

                <div>
                  <Label>{t('cars.create.fields.fuel_consumption')}</Label>
                  <Input
                    testId="car-form-field-fuel_consumption"
                    type="number"
                    value={formData.fuel_consumption}
                    onChange={(e) => setFormData(prev => ({ ...prev, fuel_consumption: parseFloat(e.target.value) }))}
                    placeholder={t('cars.create.placeholders.enter_consumption')}
                  />
                </div>

                <div>
                  <Label>{t('cars.create.fields.ecology_class')}</Label>
                  <Input
                    testId="car-form-field-ecology_class"
                    type="text"
                    value={formData.ecology_class}
                    onChange={(e) => setFormData(prev => ({ ...prev, ecology_class: e.target.value }))}
                    placeholder={t('cars.create.placeholders.enter_ecology')}
                  />
                </div>
              </div>
            </div>

            {/* Descriptions */}
            <div className="col-span-1 lg:row-span-3">
              <h3 className="text-lg font-medium mb-4 text-text-primary">{t('cars.create.descriptions')}</h3>

              <div className="space-y-4">
                <div>
                  <Label>{t('cars.create.fields.english')}</Label>
                  <TextArea
                    testId="car-form-field-description-en"
                    value={formData.description_en}
                    onChange={(value) => setFormData(prev => ({ ...prev, description_en: value }))}
                    placeholder={t('cars.create.placeholders.enter_description_en')}
                    rows={5}
                  />
                </div>

                <div>
                  <Label>{t('cars.create.fields.french')}</Label>
                  <TextArea
                    testId="car-form-field-description-fr"
                    value={formData.description_fr}
                    onChange={(value) => setFormData(prev => ({ ...prev, description_fr: value }))}
                    placeholder={t('cars.create.placeholders.enter_description_fr')}
                    rows={5}
                  />
                </div>

                <div className="relative top-[5px]">
                  <Checkbox
                    testId="car-form-field-has-gps"
                    label={t('cars.create.fields.gps')}
                    checked={formData.has_gps}
                    onChange={(checked: boolean) => setFormData(prev => ({ ...prev, has_gps: checked }))}
                  />
                </div>
              </div>
            </div>

            {/* Permitted Area */}
            <div className="col-span-1">
              <h3 className="text-lg font-medium mb-4 text-text-primary">{t('cars.create.permitted_area')}</h3>

              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-3 md:col-span-1">
                  <Label>{t('cars.create.fields.latitude')}</Label>
                  <Input
                    testId="car-form-field-latitude"
                    type="number"
                    value={formData.permitted_area_latitude}
                    onChange={(e) => setFormData(prev => ({ ...prev, permitted_area_latitude: parseFloat(e.target.value) }))}
                    placeholder={t('cars.create.placeholders.enter_latitude')}
                  />
                </div>
                <div className="col-span-3 md:col-span-1">
                  <Label>{t('cars.create.fields.longitude')}</Label>
                  <Input
                    testId="car-form-field-longitude"
                    type="number"
                    value={formData.permitted_area_longitude}
                    onChange={(e) => setFormData(prev => ({ ...prev, permitted_area_longitude: parseFloat(e.target.value) }))}
                    placeholder={t('cars.create.placeholders.enter_longitude')}
                  />
                </div>
                <div className="col-span-3 md:col-span-1">
                  <Label>{t('cars.create.fields.radius')}</Label>
                  <Input
                    testId="car-form-field-radius"
                    type="number"
                    value={formData.permitted_area_radius}
                    onChange={(e) => setFormData(prev => ({ ...prev, permitted_area_radius: parseInt(e.target.value) }))}
                    placeholder={t('cars.create.placeholders.enter_radius')}
                  />
                </div>
              </div>
            </div>

            {/* Interior & Features */}
            <div className="col-span-1 mb-4 md:mb-0">
              <h3 className="text-lg font-medium mb-4 text-text-primary">{t('cars.create.interior_features')}</h3>

              <div className="grid grid-cols-4 gap-4">
                <div className="col-span-4 md:col-span-1">
                  <Label>{t('cars.create.fields.seats')}</Label>
                  <Input
                    testId="car-form-field-seats"
                    type="number"
                    value={formData.seats_count}
                    onChange={(e) => setFormData(prev => ({ ...prev, seats_count: parseInt(e.target.value) }))}
                    placeholder={t('cars.create.placeholders.enter_seats')}
                  />
                </div>

                <div className="col-span-4 md:col-span-1">
                  <Label>{t('cars.create.fields.doors')}</Label>
                  <Input
                    testId="car-form-field-doors"
                    type="number"
                    value={formData.doors_count}
                    onChange={(e) => setFormData(prev => ({ ...prev, doors_count: parseInt(e.target.value) }))}
                    placeholder={t('cars.create.placeholders.enter_doors')}
                  />
                </div>

                <div className="col-span-4 md:col-span-1">
                  <Label>{t('cars.create.fields.clearance')}</Label>
                  <Input
                    testId="car-form-field-clearance"
                    type="number"
                    value={formData.clearance}
                    onChange={(e) => setFormData(prev => ({ ...prev, clearance: parseInt(e.target.value) }))}
                    placeholder={t('cars.create.placeholders.enter_clearance')}
                  />
                </div>

                <div className="col-span-4 md:col-span-1">
                  <Label>{t('cars.create.fields.mileage_limit')}</Label>
                  <Input
                    testId="car-form-field-mileage-limit"
                    type="number"
                    value={formData.mileage_limit}
                    onChange={(e) => setFormData(prev => ({ ...prev, mileage_limit: parseInt(e.target.value) }))}
                    placeholder={t('cars.create.placeholders.enter_mileage')}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4">
          <div className="flex items-center gap-3 sm:justify-end">
            <Button
              variant="outline"
              onClick={onClose}
            >
              {t('cars.create.buttons.cancel')}
            </Button>
            <Button
              testId="car-form-submit-button"
              variant="primary"
              onClick={handleSubmit}
              disabled={Object.keys(errors).length > 0}
            >
              {t('cars.create.buttons.create')}
            </Button>
          </div>
        </div>
      </>
    );
  }
);

CarCreateForm.displayName = 'CarCreateForm';

const ProtectedCarCreateForm = withFormProtection(CarCreateForm);

interface CarCreateFormWrapperProps extends Omit<CarCreateFormProps, 'currentData' | 'onDataChange'> {
  ref?: React.Ref<{ handleClose: () => void; isDirty: () => boolean }>;
}

const CarCreateFormWrapper = React.forwardRef<{ handleClose: () => void; isDirty: () => boolean }, CarCreateFormWrapperProps>((props, ref) => {
  const [currentData, setCurrentData] = useState<CreateCarDto>(props.initialData);

  const handleDataChange = useCallback((data: CreateCarDto) => {
    setCurrentData(data);
  }, []);

  const { initialData, ...restProps } = props;

  return (
    <ProtectedCarCreateForm
      {...restProps}
      ref={ref}
      initialData={initialData}
      currentData={currentData}
      onDataChange={handleDataChange}
    />
  );
});

CarCreateFormWrapper.displayName = 'CarCreateFormWrapper';

export default CarCreateFormWrapper;

// Export the unprotected version for testing or special cases
export { CarCreateForm as UnprotectedCarCreateForm };
